import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addComma, HOSTNAME, isExternalUrl } from "@src/utils";
import { setAgreement, weddingHallContract } from "@src/@slice/weddingHallContractSlice";
import CustomHeader from "@src/components/common/appHeader/CustomHeader";
import DataInfo from "@src/components/weddingHall/common/DataInfo";
import WeddingContractInput from "@src/components/weddingHall/reservation/WeddingContractInput";
import PriceCard from "@src/components/weddingHall/common/PriceCard";
import WeddingContract from "./policy/WeddingContract";
import FinalContractInfoModal from "@src/components/weddingHall/reservation/FinalContractInfoModal";
import Checkbox from "@src/components/common/checkbox/Checkbox";
import ProductHallCard from "@src/components/weddingHall/product/ProductHallCard";
import WeddingHallPolicies from "./policy/WeddingHallPolicies";
import useScrollLock from "@src/utils/hooks/useEnableScrollLock";
import CancelReservationModal from "@src/components/weddingHall/reservation/CancelReservationModal";

const HallContractPayment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enableScrollLock, disableScrollLock } = useScrollLock();
  const userData = useSelector(weddingHallContract).data.user;
  const hallContract = useSelector(weddingHallContract).data.weddingContractData;
  const agreementData = useSelector(weddingHallContract).data.agreement;
  const weddingContractDate = useSelector(weddingHallContract).data.weddingContractData;
  const partnerUuid = useParams().uuid;
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false); // 닫기 모달
  const [openContractModal, setOpenContractModal] = useState<boolean>(false); // 예식 계약서 창 열기
  const [checkContract, setCheckContract] = useState<boolean>(agreementData.weddingContract); // 예식 계약서 약관 check 여부
  const [paymentMethod, setPaymentMethod] = useState<"account" | "card" | null>("card"); // 결제 방법 선택
  const [btnActive, setBtnActive] = useState(
    Object.values(agreementData).every((value) => value === true),
  ); // 모든 약관에 동의하면 결제버튼 활성화

  const userInfo = [
    { key: "이름", value: [userData.contractor] },
    { key: "전화번호", value: [userData.contractorTel] },
    { key: "이메일", value: [userData.contractorEmail] },
    { key: "주소", value: [userData.contractorAddress1 + " " + userData.contractorAddress2] },
  ];
  const coupleInfo = [
    { key: "신부", value: [userData.brideName, userData.brideTel] },
    { key: "신랑", value: [userData.groomName, userData.groomTel] },
  ];
  const priceInfo = [];
  if (hallContract.totalAmount > hallContract.finalAmount) {
    // 최종금액이 총 금액 보다 작을 때(할인 금액이 있을)
    priceInfo.push({ key: "총 금액", value: [`${addComma(hallContract.totalAmount)}원`] });
    priceInfo.push({
      key: "할인금액",
      value: [`${addComma(hallContract.totalAmount - hallContract.finalAmount)}원`],
      style: { color: "#F53F29" },
    });
  }
  priceInfo.push({ key: "최종금액", value: [addComma(hallContract.finalAmount) + "원"] });

  const selectPaymentMethod = (e) => {
    if (paymentMethod === e.target.value) {
      setPaymentMethod(null);
    } else {
      setPaymentMethod(e.target.value);
    }
  };

  useEffect(() => setCheckContract(agreementData.weddingContract), [agreementData.weddingContract]);
  useEffect(() => {
    setBtnActive(Object.values(agreementData).every((value) => value === true));
  }, [agreementData]);

  return (
    <>
      <div className="page-contract-payment">
        <CustomHeader
          close={() => setShowCancelModal(true)}
          goBack={() => {
            navigate(`/weddinghall/${partnerUuid}/contract?step=1`, { replace: true });
          }}
        />
        <div className="hall-info-section">
          <h1>{`정보 확인 후 \n결제를 완료해주세요.`}</h1>
          <ProductHallCard
            hallInfo={{
              name: weddingContractDate.partnerName,
              finalAmount: weddingContractDate.finalAmount,
              weddingDate: weddingContractDate.weddingDate,
              weddingTime: weddingContractDate.weddingTime,
              guarantee: weddingContractDate.guarantee,
              discountRate: weddingContractDate.discountRate,
              weddingDayId: weddingContractDate.weddingDayId,
              image: weddingContractDate.images[0],
            }}
            hasLike={false}
          />
        </div>
        <div className="divider" style={{ margin: "20px 0" }} />
        <div className="contract-info-section">
          <p className="title" style={{ padding: "14px 0" }}>
            필수확인사항
          </p>
          <WeddingContractInput
            clickHandler={() => {
              setOpenContractModal(true);
              enableScrollLock();
            }}
            checkedHandler={() => {
              if (checkContract) {
                setCheckContract(false);
                dispatch(setAgreement({ keyname: "weddingContract", checked: false }));
              }
            }}
            checked={checkContract}
          />
        </div>
        <div className="divider" style={{ margin: "20px 0" }} />
        <div className="user-info-section">
          <p className="title">예식정보</p>
          <DataInfo title="계약자 정보" datas={userInfo} />
          <DataInfo title="예식자 정보" datas={coupleInfo} />
          <DataInfo title="가격 정보" datas={priceInfo} />
        </div>
        <div className="divider" style={{ margin: "20px 0" }} />
        <div className="payment-info-section">
          <p className="title">판매자 정보</p>
          <DataInfo
            title=""
            datas={[
              { key: "상호", value: hallContract.sellerInfo.partnerName || "" },
              { key: "주소", value: hallContract.sellerInfo.address || "" },
              { key: "사업자등록번호", value: hallContract.sellerInfo.bizNumber || "" },
            ]}
            divider={false}
          />
        </div>
        <div className="divider" style={{ margin: "20px 0" }} />
        <div className="payment-info-section">
          <p className="title">계약할금액</p>
          <DataInfo
            title=""
            datas={[
              {
                key: "계약금",
                value: addComma(hallContract.deposit) + "원",
                style: { color: "#F53F29", fontSize: "18px", fontWeight: "bold" },
              },
            ]}
            divider={false}
          />
          <PriceCardWrap price={hallContract.deposit} />
        </div>
        <div className="divider" style={{ margin: "0 0 20px 0" }} />
        <div className="payment-info-section2">
          <p className="title">결제수단</p>
          <div className="btn-group">
            {/* <button className={`btn btn-large btn-outline-gray200 ${paymentMethod === 'account'? 'selected': ''}`} value='account' onClick={(e)=>selectPaymentMethod(e)}>계좌결제</button> */}
            <button
              className={`btn btn-large btn-outline-gray200 ${paymentMethod === "card" ? "selected" : ""}`}
              value="card"
              onClick={(e) => selectPaymentMethod(e)}
            >
              카드결제
            </button>
          </div>
        </div>
        <div className="divider" style={{ margin: "20px 0" }} />
        <div className="agree-info-section">
          <AgreePaymentBtnWrap
            price={hallContract.deposit}
            paymentMethod={paymentMethod}
            active={btnActive}
          />
        </div>
        <CancelReservationModal isShow={showCancelModal} onClose={setShowCancelModal} />
      </div>
      <>
        {openContractModal && (
          <WeddingContract
            isOpen={openContractModal}
            setClose={() => {
              setOpenContractModal(!openContractModal);
              disableScrollLock();
            }}
          />
        )}
      </>
    </>
  );
};

export default HallContractPayment;

const PriceCardWrap = ({ price }) => {
  return (
    <div className="payment-process-wrap">
      <p className="title">결제과정</p>
      <div>
        <PriceCard type="now" title={"계약금"} price={price} date="지금 결제" />
        <PriceCard
          type="after"
          title={"잔금 결제"}
          noti={`계약금을 제외한 잔금은 예식 당일에 웨딩홀에서 직접 결제합니다.\n 예식 당일 하객수를 반영하여 잔금이 최종 결정됩니다.`}
        />
      </div>
      <ul>
        <li>
          예식 당일 하객 인원 기준으로 예식 총 진행 금액을 계산하여 계약금을 제외한 잔금을
          결제하시면 됩니다.
          <br />
          (총 진행금액 - 계약금 = 잔금)
        </li>
      </ul>
    </div>
  );
};

const AgreePaymentBtnWrap = ({ price, paymentMethod, active }) => {
  const dispatch = useDispatch();
  const agreement = useSelector(weddingHallContract).data.weddingContractData.agreement;
  const isAgree = useSelector(weddingHallContract).data.agreement.personalPolicy; //personalPolicy(개인정보 수집) 동의여부

  const [isCheck, setIsCheck] = useState(!!isAgree);
  const [openPolicyModal, setOpenPolicyModal] = useState({
    target: "",
    open: false,
    onlyRead: false,
    policy: "",
  });
  const [finalInfoModal, setFinalInfoModal] = useState<boolean>(false); // 결제하기 버튼 클릭 후 마지막 정보 확인 바텀시트

  useEffect(() => setIsCheck(isAgree), [isAgree]);

  const closePolicyModal = () =>
    setOpenPolicyModal({ ...openPolicyModal, open: false, target: "" });
  const clickPayment = () => setFinalInfoModal(true);

  return (
    <>
      <div className="agree-payment-wrap">
        <div className="agree-wrap">
          <Checkbox
            id="personal-policy-check"
            name="personal-policy-check"
            value="개인정보수집 및 개인정보제공, 제 3자 제공동의를 모두 읽었으며 이에 동의합니다."
            setValue={() => {
              setIsCheck(!isCheck);
              dispatch(setAgreement({ keyname: "personalPolicy", checked: !isCheck }));
            }}
            checked={isCheck}
          />
          <p>
            <span
              className="policy"
              onClick={() =>
                setOpenPolicyModal({ target: "0", open: true, onlyRead: false, policy: agreement })
              }
            >
              개인정보수집 및 이용
            </span>
            ,
            <span
              className="policy"
              onClick={() =>
                setOpenPolicyModal({ target: "1", open: true, onlyRead: false, policy: agreement })
              }
            >
              {` 개인정보 제3자 제공 동의`}
            </span>
            를 모두 읽었으며 이에 동의합니다.
          </p>
        </div>
        <div className="divider-line" />
        <div className="payment-notice">
          <p>(주)웨딩북은 통신판매중개자이며 통신판매의 당사자가 아닙니다.</p>
          <p>
            (주)웨딩북은 예약 및 구매관련 통신판매업자가 제공하는 상품, 거래정보 및 거래 등에 대하여
            책임을 지지 않습니다.
          </p>
        </div>
        <button
          className={`btn btn-full btn-red500 ${active && paymentMethod !== null ? "" : "btn-disabled"}`}
          disabled={!active}
          onClick={clickPayment}
        >
          계약금 {price?.toLocaleString()}원 결제
        </button>
      </div>
      <WeddingHallPolicies openModalInfo={openPolicyModal} closeModal={closePolicyModal} />
      <FinalContractInfoModal
        isShow={finalInfoModal}
        price={price}
        closeModal={() => setFinalInfoModal(false)}
        paymentMethod={paymentMethod}
      />
    </>
  );
};
